import { useContext, useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import rolePermissions from "../config/rolePermissions";

import {
  Home, Settings, BarChart, Calculator, Headphones, LogOut, ChevronRight, Users,
  Timer, FileText, Mic, Bot, Bolt, KeyRound, Megaphone, Network, Grid2X2, MessageCircle, ClipboardList,BookOpen,
} from "lucide-react";

const routeIcons = {
  "/home": Home,
  "/agent-assist": Headphones,
  "/rebate-calculator": Calculator,
  "/gnosis": Timer,
  "/user-management": Users,
  "/admin/announcements": Megaphone,
  "/rebate-viewer": FileText,
  "/audio-transcriber": Mic,
  "/agent-assist-gpt4o": Bolt,
  "/hrpay": Bot,
  "/settings": Settings,
  "/idp-monitoring": BarChart,
  "/password-manager": KeyRound,
  "/qa-audio-transcriber": Headphones,
  "/resume-analysis": FileText,
  "/network-analysis": Network,
  "/coaching-transcriber": Headphones,
  "/webview": Network,
  "/learning": BookOpen,
  "/tools": Grid2X2,
  "/chat": MessageCircle,
  "/audio-sentiment": BarChart,
  "/esatsurvey": ClipboardList,
};

const routeLabels = {
  "/home": "Home",
  "/agent-assist": "Agent Assist",
  "/rebate-calculator": "Rebate Calculator",
  "/gnosis": "Gnosis",
  "/user-management": "Access Management",
  "/admin/announcements": "Announcements",
  "/rebate-viewer": "Rebate Viewer",
  "/audio-transcriber": "MOM Generator",
  "/agent-assist-gpt4o": "AI Arena",
  "/hrpay": "HRPay",
  "/settings": "Settings",
  "/idp-monitoring": "IDP Monitoring",
  "/password-manager": "Password Manager",
  "/qa-audio-transcriber": "QA Transcriber",
  "/resume-analysis": "Resume Analysis",
  "/network-analysis": "Network Analysis",
  "/coaching-transcriber": "Coaching Transcriber",
  "/webview": "Method Of Procedure Ai",
  "/tools": "Tools Overview",
  "/chat": "U-Connect(Development)",
  "/audio-sentiment": "Audio Sentiment",
  "/esatsurvey": "Survey Tool",
  "/learning": "Learning", 
};

const STORAGE_KEY = "sidebarConfig:v1";


const Sidebar = ({ onExpandChange }) => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const { darkMode } = useDarkMode();
  const [isExpanded, setIsExpanded] = useState(false);

  // User config (localStorage)
  const [config, setConfig] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  // Editor inputs (dark-mode friendly; no prompts)
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [newLinkLabel, setNewLinkLabel] = useState("");

  useEffect(() => {
    if (onExpandChange) onExpandChange(isExpanded);
  }, [isExpanded, onExpandChange]);

  // Build accessible routes (minus /home and /settings) after role is known
  useEffect(() => {
    if (!user?.role) return;
    const accessibleRoutes = Object.entries(rolePermissions)
      .filter(([path, roles]) => roles.includes(user.role) && path !== "/home" && path !== "/settings")
      .map(([path]) => path);

    const saved = localStorage.getItem(STORAGE_KEY);
    let cfg = saved ? JSON.parse(saved) : {
      order: accessibleRoutes.slice().sort((a, b) => (routeLabels[a] || a).localeCompare(routeLabels[b] || b)),
      hidden: [],
      links: [],
    };

    // Merge order with latest accessible list
    const mergedOrder = cfg.order.filter((p) => accessibleRoutes.includes(p));
    for (const p of accessibleRoutes) if (!mergedOrder.includes(p)) mergedOrder.push(p);

    // Clean hidden list
    const hidden = cfg.hidden.filter((p) => mergedOrder.includes(p));

    cfg = { ...cfg, order: mergedOrder, hidden };
    setConfig(cfg);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  }, [user?.role]);

  const saveConfig = (next) => {
    setConfig(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const toggleSidebar = () => setIsExpanded((prev) => !prev);

  const getNavLinkClasses = (path) => {
    const isActive = location.pathname === path;
    const base = "flex items-center p-2.5 rounded-xl transition-all duration-300 group relative overflow-hidden";
    const layout = isExpanded ? "" : "justify-center";

    if (isActive) {
      return `${base} ${layout} bg-gradient-to-r from-gray-700 to-gray-800 dark:from-gray-800 dark:to-gray-700 text-white dark:text-uasYellow shadow-lg hover:shadow-xl transform hover:scale-105`;
    }
    return `${base} ${layout} bg-white/40 dark:bg-white/5 backdrop-blur-md border border-white/20 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-white/10 hover:border-white/30 dark:hover:border-white/20 hover:shadow-md hover:scale-105`;
  };

  const renderNavLink = (path) => {
    const Icon = routeIcons[path];
    const label = routeLabels[path] || path;
    const isActive = location.pathname === path;

    return (
      <NavLink to={path} className={() => getNavLinkClasses(path)} title={label}>
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-pink-500/0 to-orange-500/0 group-hover:from-purple-500/10 group-hover:via-pink-500/10 group-hover:to-orange-500/10 transition-all duration-300"></div>
        {Icon && (
          <Icon
            className={[
              "relative w-5 h-5 transition-transform duration-300",
              darkMode
                ? "text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]"
                : "text-black drop-shadow-[0_1px_1px_rgba(0,0,0,0.10)]",
              isActive ? "scale-110" : "group-hover:scale-110",
            ].join(" ")}
          />
        )}
        {isExpanded && (
          <span className={`relative ml-3 font-medium text-sm transition-all duration-300 ${isActive ? "font-semibold" : ""}`}>
            {label}
          </span>
        )}
      </NavLink>
    );
  };

  const renderNavLinks = () => {
    if (!config) return null;
    const visibleRoutes = config.order.filter((p) => !config.hidden.includes(p));
    return visibleRoutes.map((path) => (
      <li key={path}>{renderNavLink(path)}</li>
    ));
  };

  return (
    <>
      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <aside
        id="logo-sidebar"
        className={`fixed font-montserrat top-0 left-0 z-40 h-screen pt-[60px] transition-all duration-300 ${isExpanded ? "w-64" : "w-20"}`}
        aria-label="Sidebar"
      >
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/80 to-gray-50/70 dark:from-[#2D2D2D]/90 dark:via-[#2D2D2D]/80 dark:to-[#2D2D2D]/70"></div>
        <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 bg-white/30 dark:bg-[#2D2D2D]/30"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
        <div className="absolute top-0 right-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-gray-300/50 to-transparent dark:via-transparent"></div>

        {/* Toggle button */}
        <div
          className={`absolute -right-4 top-24 flex items-center justify-center w-9 h-9 bg-white/80 dark:bg-[#2D2D2D]/80 backdrop-blur-md border border-white/20 dark:border-gray-700/20 rounded-full cursor-pointer shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 ${isExpanded ? "rotate-180" : ""} group`}
          onClick={toggleSidebar}
          title={isExpanded ? "Collapse" : "Expand"}
        >
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/20 group-hover:to-pink-500/20 transition-all duration-300"></div>
          <ChevronRight className="relative w-5 h-5 text-gray-700 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
        </div>

        {/* Customize button */}
        {isExpanded && (
          <button
            onClick={() => setShowEditor(true)}
            className="absolute -right-4 top-40 w-9 h-9 bg-white/80 dark:bg-[#2D2D2D]/80 border border-white/20 dark:border-gray-700/20 rounded-full shadow hover:shadow-lg"
            title="Customize Sidebar"
          >
            ✨
          </button>
        )}

        <div className="relative h-full px-4 pb-4 flex flex-col">
          {/* Home */}
          {rolePermissions["/home"]?.includes(user.role) && (
            <div className="pt-4 pb-2">{renderNavLink("/home")}</div>
          )}

          {rolePermissions["/home"]?.includes(user.role) && (
            <div className="mx-2 mb-2 border-t border-gray-200/50 dark:border-gray-700/50"></div>
          )}

          {/* Main navigation */}
          <ul className="space-y-2 font-medium flex-1 overflow-y-auto hide-scrollbar">
            {renderNavLinks()}
          </ul>

          {/* Shortcuts */}
          {config?.links?.length > 0 && (
            <>
              <div className="mx-2 mt-2 mb-2 border-t border-gray-200/50 dark:border-gray-700/50"></div>
              <ul className="space-y-2">
                {config.links.map((l) => (
                  <li key={l.id}>
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                      className={getNavLinkClasses("external")}
                      title={l.label}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-pink-500/0 to-orange-500/0 group-hover:from-purple-500/10 group-hover:via-pink-500/10 group-hover:to-orange-500/10 transition-all duration-300"></div>
                      <Network
                        className={[
                          "relative w-5 h-5 transition-transform duration-300 group-hover:scale-110",
                          darkMode
                            ? "text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]"
                            : "text-black drop-shadow-[0_1px_1px_rgba(0,0,0,0.10)]",
                        ].join(" ")}
                      />
                      {isExpanded && <span className="relative ml-3 font-medium text-sm">{l.label}</span>}
                    </a>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Settings */}
          {rolePermissions["/settings"]?.includes(user.role) && (
            <>
              <div className="mx-2 mt-2 mb-2 border-t border-gray-200/50 dark:border-gray-700/50"></div>
              <div className="pb-2">{renderNavLink("/settings")}</div>
            </>
          )}

          {/* Logout */}
          <div className="pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
            <button
              onClick={logout}
              className={`w-full flex items-center p-2.5 rounded-xl transition-all duration-300 group relative overflow-hidden bg-red-200/70 dark:bg-red-900/30 border border-red-300/70 dark:border-red-700/40 text-red-600 dark:text-red-400 hover:bg-red-300/70 dark:hover:bg-red-800/40 hover:border-red-400/70 dark:hover:border-red-600/50 hover:shadow-md hover:scale-105 ${isExpanded ? "" : "justify-center"}`}
              title="Logout"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-red-400/0 to-pink-400/0 group-hover:from-red-400/15 group-hover:to-pink-400/15 transition-all duration-300"></div>
              <LogOut className="relative w-5 h-5 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
              {isExpanded && <span className="relative ml-3 font-medium text-sm">Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Dark-mode aware Customize Overlay */}
      {showEditor && config && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-[560px] max-h-[80vh] overflow-auto rounded-2xl border
                          bg-white text-gray-900 shadow-2xl
                          dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-800">
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between
                            border-b px-4 py-3
                            bg-white/80 backdrop-blur
                            dark:bg-neutral-900/80 dark:border-neutral-800">
              <h3 className="text-lg font-semibold">Customize Sidebar</h3>
              <button
                onClick={() => setShowEditor(false)}
                className="rounded-lg border px-3 py-1.5 text-sm
                           hover:bg-neutral-50 active:scale-[0.98]
                           dark:border-neutral-700 dark:hover:bg-neutral-800"
              >
                Close
              </button>
            </div>

            <div className="space-y-8 p-4">
              {/* Reorder + Hide/Show */}
              <section>
                <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide
                               text-neutral-600 dark:text-neutral-400">
                  Main Items
                </h4>
                <ul className="space-y-2">
                  {config.order.map((path, idx) => {
                    const label = routeLabels[path] || path;
                    const hidden = config.hidden.includes(path);
                    return (
                      <li
                        key={path}
                        className="flex items-center justify-between rounded-xl border p-2.5
                                   bg-white/70 hover:bg-neutral-50
                                   dark:bg-neutral-900/60 dark:hover:bg-neutral-900
                                   border-neutral-200 dark:border-neutral-800"
                      >
                        <div className="truncate">
                          <div className="font-medium">{label}</div>
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">{path}</div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            className="rounded-lg border px-2.5 py-1 text-sm
                                       hover:bg-neutral-50 disabled:opacity-40
                                       active:scale-[0.98]
                                       dark:border-neutral-700 dark:hover:bg-neutral-800"
                            disabled={idx === 0}
                            onClick={() => {
                              const next = { ...config, order: [...config.order] };
                              [next.order[idx - 1], next.order[idx]] = [next.order[idx], next.order[idx - 1]];
                              saveConfig(next);
                            }}
                          >
                            Up
                          </button>
                          <button
                            className="rounded-lg border px-2.5 py-1 text-sm
                                       hover:bg-neutral-50 disabled:opacity-40
                                       active:scale-[0.98]
                                       dark:border-neutral-700 dark:hover:bg-neutral-800"
                            disabled={idx === config.order.length - 1}
                            onClick={() => {
                              const next = { ...config, order: [...config.order] };
                              [next.order[idx + 1], next.order[idx]] = [next.order[idx], next.order[idx + 1]];
                              saveConfig(next);
                            }}
                          >
                            Down
                          </button>
                          <button
                            className={`rounded-lg border px-2.5 py-1 text-sm active:scale-[0.98]
                                        ${hidden ? "hover:bg-green-50 dark:hover:bg-green-900/30" : "hover:bg-neutral-50 dark:hover:bg-neutral-800"}
                                        dark:border-neutral-700`}
                            onClick={() => {
                              const next = { ...config };
                              next.hidden = hidden
                                ? next.hidden.filter((p) => p !== path)
                                : [...next.hidden, path];
                              saveConfig(next);
                            }}
                          >
                            {hidden ? "Show" : "Hide"}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>

              {/* Shortcuts */}
              <section>
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-sm font-semibold uppercase tracking-wide
                                 text-neutral-600 dark:text-neutral-400">
                    Shortcuts
                  </h4>
                </div>

                {/* Add shortcut form (dark-aware) */}
                <form
                  className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_minmax(140px,0.5fr)_auto]"
                  onSubmit={(e) => {
                    e.preventDefault();
                    let url = (newLinkUrl || "").trim();
                    if (!url) return;
                    if (!/^https?:\/\//i.test(url)) url = "https://" + url; // normalize bare domains
                    const label = (newLinkLabel || newLinkUrl).trim();
                    const next = { ...config, links: [...config.links, { id: String(Date.now()), label, url }] };
                    saveConfig(next);
                    setNewLinkUrl("");
                    setNewLinkLabel("");
                  }}
                >
                  <input
                    value={newLinkUrl}
                    onChange={(e) => setNewLinkUrl(e.target.value)}
                    placeholder="https://example.com or example.com"
                    className="w-full rounded-xl border px-3 py-2 outline-none
                               placeholder:text-neutral-400
                               focus:ring-2 focus:ring-purple-400/60
                               border-neutral-200 bg-white
                               dark:border-neutral-700 dark:bg-neutral-900 dark:placeholder:text-neutral-500"
                  />
                  <input
                    value={newLinkLabel}
                    onChange={(e) => setNewLinkLabel(e.target.value)}
                    placeholder="Label (optional)"
                    className="w-full rounded-xl border px-3 py-2 outline-none
                               placeholder:text-neutral-400
                               focus:ring-2 focus:ring-purple-400/60
                               border-neutral-200 bg-white
                               dark:border-neutral-700 dark:bg-neutral-900 dark:placeholder:text-neutral-500"
                  />
                  <button
                    type="submit"
                    className="rounded-xl border px-4 py-2 font-medium
                               bg-neutral-50 hover:bg-neutral-100 active:scale-[0.98]
                               dark:border-neutral-700 dark:bg-neutral-800 dark:hover:bg-neutral-700"
                  >
                    + Add
                  </button>
                </form>

                {/* Shortcut list */}
                <ul className="space-y-2">
                  {config.links.map((l) => (
                    <li
                      key={l.id}
                      className="flex items-center justify-between rounded-xl border p-2.5
                                 bg-white/70 hover:bg-neutral-50
                                 dark:bg-neutral-900/60 dark:hover:bg-neutral-900
                                 border-neutral-200 dark:border-neutral-800"
                    >
                      <div className="truncate">
                        <div className="font-medium">{l.label}</div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">{l.url}</div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="rounded-lg border px-2.5 py-1 text-sm
                                     hover:bg-neutral-50 active:scale-[0.98]
                                     dark:border-neutral-700 dark:hover:bg-neutral-800"
                          onClick={() => {
                            const label = window.prompt("Rename label:", l.label);
                            if (!label) return;
                            const next = { ...config, links: config.links.map((x) => (x.id === l.id ? { ...x, label } : x)) };
                            saveConfig(next);
                          }}
                        >
                          Rename
                        </button>
                        <button
                          className="rounded-lg border px-2.5 py-1 text-sm text-red-600
                                     hover:bg-red-50 active:scale-[0.98]
                                     dark:text-red-400 dark:border-neutral-700 dark:hover:bg-red-900/20"
                          onClick={() => {
                            const next = { ...config, links: config.links.filter((x) => x.id !== l.id) };
                            saveConfig(next);
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
